import { Link } from 'react-router-dom';
import { ArrowRight, Clock, BadgeCheck } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import type { GuideMeta } from '@/content/guides/types';
import { GUIDE_CATEGORIES, categoryVars } from '@/content/guides/types';

/**
 * Guide card. `featured` renders the wide editorial treatment used for the lead
 * story; the default is the 3-up grid card.
 */
export default function GuideCard({
  guide,
  featured = false,
  className,
}: {
  guide: GuideMeta;
  featured?: boolean;
  className?: string;
}) {
  const category = GUIDE_CATEGORIES[guide.category];

  return (
    <Link
      to={`/guides/${guide.slug}`}
      style={categoryVars(category)}
      className={twMerge(
        'group flex overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm',
        'transition-all duration-200 hover:-translate-y-1 hover:shadow-md',
        'dark:border-surface-dark-border dark:bg-surface-dark-card',
        featured ? 'flex-col sm:flex-row' : 'flex-col',
        className
      )}
    >
      {/* Cover */}
      <div
        className={twMerge(
          'relative flex flex-shrink-0 items-end bg-gradient-to-br p-4',
          guide.gradient,
          featured ? 'min-h-[160px] sm:w-2/5' : 'h-[132px]'
        )}
      >
        <span
          lang="ko"
          className="font-korean text-sm font-medium text-white/70"
          aria-hidden="true"
        >
          {category.labelKo}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-[var(--cat-text)] dark:text-[var(--cat-text-dark)]">
            {category.label}
          </span>
          {guide.factChecked && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 dark:text-green-500">
              <BadgeCheck className="h-3 w-3" aria-hidden="true" />
              Fact-checked
            </span>
          )}
        </div>

        <h3
          className={twMerge(
            'font-bold leading-snug text-ink-900 dark:text-ink-50',
            featured ? 'text-xl' : 'text-base'
          )}
        >
          {guide.shortTitle}
        </h3>

        <p
          className={twMerge(
            'mt-2 flex-1 text-sm leading-6 text-ink-500 dark:text-ink-400',
            featured ? 'line-clamp-4' : 'line-clamp-3'
          )}
        >
          {featured ? guide.excerpt : guide.description}
        </p>

        <div className="mt-4 flex items-center justify-between text-xs text-ink-400">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3 w-3" aria-hidden="true" />
            {guide.readingTimeMinutes} min read
          </span>
          <span className="inline-flex items-center gap-1 font-medium text-cheong-500 group-hover:gap-2 dark:text-cheong-dark [transition:gap_150ms_ease-out]">
            Read guide
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}
